// src/pages/finance-portal/bill-payments/BillPayments.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination, Chip, IconButton,
    Alert, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, InputAdornment, Autocomplete,
    CircularProgress,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PAYMENT_METHODS = [
    { value: 'ACH', label: 'ACH' },
    { value: 'CHECK', label: 'Check' },
    { value: 'WIRE', label: 'Wire Transfer' },
    { value: 'VIRTUAL_CARD', label: 'Virtual Card' },
];

export default function BillPayments() {
    const { enqueueSnackbar } = useSnackbar();
    const [payments, setPayments] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [bills, setBills] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [form, setForm] = useState({
        bill_id: null, payment_date: new Date().toISOString().split('T')[0],
        amount: '', payment_method: 'ACH', reference_number: '', memo: '', gl_account_id: null,
    });

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, payment: null });
    const [deleting, setDeleting] = useState(false);

    const fetchPayments = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/bill-payments', { params: { page: page + 1, per_page: rowsPerPage } });
            if (res.data.success) {
                setPayments(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load payments');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage]);

    const fetchBills = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/bills', { params: { per_page: 100, status: 'APPROVED' } });
            if (res.data.success) {
                // Include PARTIALLY_PAID bills too
                const res2 = await axiosInstance.get('/api/finance/bills', { params: { per_page: 100, status: 'PARTIALLY_PAID' } });
                const allBills = [...(res.data.data || []), ...(res2.data?.data || [])];
                setBills(allBills);
            }
        } catch (err) { /* silent */ }
    }, []);

    const fetchGlAccounts = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/coa?flat=true');
            if (res.data.success) setGlAccounts(res.data.data || []);
        } catch (err) { /* silent */ }
    }, []);

    useEffect(() => { fetchPayments(); }, [fetchPayments]);
    useEffect(() => { fetchBills(); fetchGlAccounts(); }, [fetchBills, fetchGlAccounts]);

    const handleCreate = async () => {
        if (!form.bill_id || !form.amount) {
            enqueueSnackbar('Bill and amount are required', { variant: 'warning' });
            return;
        }
        setSaving(true);
        try {
            const res = await axiosInstance.post('/api/finance/bill-payments', {
                bill_id: form.bill_id,
                payment_date: form.payment_date,
                amount: parseFloat(form.amount),
                payment_method: form.payment_method,
                reference_number: form.reference_number || null,
                memo: form.memo || null,
                gl_account_id: form.gl_account_id,
            });
            if (res.data.success) {
                enqueueSnackbar('Payment recorded successfully', { variant: 'success' });
                setCreateOpen(false);
                setForm({ bill_id: null, payment_date: new Date().toISOString().split('T')[0], amount: '', payment_method: 'ACH', reference_number: '', memo: '', gl_account_id: null });
                fetchPayments();
                fetchBills();
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to record payment', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/bill-payments/${deleteDialog.payment.id}`);
            setDeleteDialog({ open: false, payment: null });
            fetchPayments();
            fetchBills();
            enqueueSnackbar('Payment deleted', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to delete payment', { variant: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const selectedBill = bills.find(b => b.id === form.bill_id);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PaymentsIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Bill Payments
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                Track vendor payments
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                    >
                        Record Payment
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Payment #', 'Bill #', 'Vendor', 'Date', 'Amount', 'Method', 'Reference', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [...Array(6)].map((_, i) => <TableRow key={i}><TableCell colSpan={8}><Skeleton height={36} /></TableCell></TableRow>)
                                ) : payments.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <PaymentsIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No payments recorded</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : payments.map(p => (
                                    <TableRow key={p.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{p.payment_number}</TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{p.bill_number || '—'}</TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{p.vendor_name || '—'}</TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatDate(p.payment_date)}</TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(p.amount)}</TableCell>
                                        <TableCell><Chip label={p.payment_method} size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22 }} /></TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{p.reference_number || '—'}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => setDeleteDialog({ open: true, payment: p })} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Paper>
            </Box>

            {/* Record Payment Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Record Bill Payment</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Autocomplete
                            options={bills}
                            getOptionLabel={b => b ? `${b.bill_number} — ${b.vendor_name || ''} (Due: ${formatCurrency(b.balance_due)})` : ''}
                            value={bills.find(b => b.id === form.bill_id) || null}
                            onChange={(_, val) => setForm(prev => ({ ...prev, bill_id: val?.id || null, amount: val ? String(val.balance_due) : '' }))}
                            renderInput={params => <TextField {...params} label="Select Bill" size="small" sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }} />}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        />
                        {selectedBill && (
                            <Alert severity="info" sx={{ borderRadius: '10px', fontFamily, fontSize: '0.8rem' }}>
                                Balance due: <strong>{formatCurrency(selectedBill.balance_due)}</strong> | Total: {formatCurrency(selectedBill.total_amount)} | Paid: {formatCurrency(selectedBill.amount_paid)}
                            </Alert>
                        )}
                        <TextField label="Payment Date" size="small" type="date" value={form.payment_date}
                            onChange={e => setForm(prev => ({ ...prev, payment_date: e.target.value }))}
                            InputLabelProps={{ shrink: true }} sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }} />
                        <TextField label="Amount" size="small" type="number" value={form.amount}
                            onChange={e => setForm(prev => ({ ...prev, amount: e.target.value }))}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, sx: { fontFamily, borderRadius: '10px' } }} />
                        <FormControl size="small">
                            <InputLabel sx={{ fontFamily }}>Payment Method</InputLabel>
                            <Select value={form.payment_method} label="Payment Method"
                                onChange={e => setForm(prev => ({ ...prev, payment_method: e.target.value }))}
                                sx={{ fontFamily, borderRadius: '10px' }}>
                                {PAYMENT_METHODS.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontFamily }}>{m.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Autocomplete
                            options={glAccounts}
                            getOptionLabel={a => a ? `${a.account_number} - ${a.name}` : ''}
                            value={glAccounts.find(a => a.id === form.gl_account_id) || null}
                            onChange={(_, val) => setForm(prev => ({ ...prev, gl_account_id: val?.id || null }))}
                            renderInput={params => <TextField {...params} label="Cash/Bank Account" size="small" sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }} />}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        />
                        <TextField label="Reference #" size="small" value={form.reference_number}
                            onChange={e => setForm(prev => ({ ...prev, reference_number: e.target.value }))}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        <TextField label="Memo" size="small" multiline rows={2} value={form.memo}
                            onChange={e => setForm(prev => ({ ...prev, memo: e.target.value }))}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCreateOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? 'Saving...' : 'Record Payment'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, payment: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Payment?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>
                        This will reverse payment {deleteDialog.payment?.payment_number} of {formatCurrency(deleteDialog.payment?.amount)} and update the bill balance.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, payment: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDelete} disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
